import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { GuidePanel, ContextHelpButton, OnboardingTour, shouldShowInitialTour } from "../components/guide/GuideSystem";
import { pageKeyByPath, type GuidePageKey } from "../config/guideContent";
import { Icon } from "../components/ui/Icon";
import { ActionButton, AlertCard, EmptyState, ErrorState, LoadingState, MetricCard, PageHeader, SearchInput, StatusPill } from "../components/operations/OperationalComponents";
import { CommandPalette } from "../components/command/CommandPalette";
import * as Legacy from "../app/runtime";

const { localPreviewPorts, apiBaseUrl, http, getUser, setAuth, clearAuth, getErr, fmt, fmtDate, fmtDateFull, fmtTime, fmtRel, urgColor, urgLabel, todayStr, startOfDayIso, endOfDayIso, genKey, safePhone, waUrl, mapsUrl, telUrl, openExternal, BRAND_LOGO, TOK, C, EXECUTIVE_ROLES, MANAGER_ROLES, SALES_ROLES, OPS_ROLES, ADMIN_ROLES, WHATSAPP_ROLES, FOLLOW_UP_ROLES, ROLE_LABELS, roleLabel, hasAnyRole, CSS, BrandLogo, ICON_NAMES, renderIcon, Btn, Input, Card, Modal, Toast, Skeleton, Badge, StatusBadge, KPI, SH, Tabs, Row, Empty, useToast, QO, useOrders, useOrder, useCustSrch, useCust, useNotifs, useRules, useCal, useMemory, useFollowUps, useRevenue, useRisks, useStaff, useFeedback, useAssist, useHealth, useUsers, useApprovals, useAudit, useDelivery, useInvoices, useMktRpt, useCharge, useSearch, useInquiries, useGST, useFollowUpTasks, useWhatsAppConversations, useWhatsAppConversation, useWhatsAppAssignableUsers, GlobalSearch, NotifBell, PIPE, StatusPipeline, CancelModal, RescheduleModal, RiderModal, OrderCommandCenter, NewOrderForm, OrderCard, LOST_REASONS, NewInquiryForm, InquiryConversationModal, QuickActions, NAV_MAP } = Legacy;

export const AppShell = () => {
  const [user, setUserState] = useState<any>(() => getUser()); const nav = useNavigate(); const loc = useLocation();
  const [checkingSession, setCheckingSession] = useState(true);
  const [sidebar, setSidebar] = useState(true); const [newOrd, setNewOrd] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(() => shouldShowInitialTour());
  const qc = useQueryClient();
  useEffect(() => {
    let active = true;
    http.get("/auth/me")
      .then((res) => {
        const nextUser = res.data?.data?.user;
        if (nextUser) {
          setAuth(nextUser);
          if (active) setUserState(nextUser);
        }
      })
      .catch(() => {
        clearAuth();
        if (active) setUserState(null);
      })
      .finally(() => { if (active) setCheckingSession(false); });
    return () => { active = false; };
  }, []);
  const logout = async () => { try { await http.post("/auth/logout"); } catch {} clearAuth(); qc.clear(); setUserState(null); nav("/login"); };
  if (checkingSession) return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: C.bg, color: C.mid, fontSize: 13 }}>Checking session...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  const items = NAV_MAP[user.role] || NAV_MAP.SALES;
  const pageKey = pageKeyByPath(loc.pathname);
  const pageTitle = items.find(([path]: any) => path === loc.pathname)?.[1] || {
    dashboard: "Command Center",
    "follow-ups": "Follow-ups",
    whatsapp: "WhatsApp",
    customers: "Customers",
    orders: "Orders",
    production: "Production",
    dispatch: "Dispatch",
    finance: "Finance",
    marketing: "Marketing",
    intelligence: "Intelligence",
    calendar: "Calendar",
    admin: "Control",
    profile: "Profile",
  }[pageKey as Exclude<GuidePageKey, "login" | "initial-setup" | "accept-invitation" | "change-password">] || "Command Center";
  const goTo = (path: string) => {
    nav(path);
    setMobileMenuOpen(false);
  };
  return (
    <div style={{ display: "flex", height: "100vh", background: C.bg, overflow: "hidden" }}>
      <style>{CSS}</style>

      {/* Sidebar — desktop */}
      <aside className="desktop-sidebar" style={{ width: sidebar ? 248 : 64, background: "rgba(255,255,255,0.82)", backdropFilter: "blur(18px)", borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column" as any, transition: "width .22s ease", flexShrink: 0, overflow: "hidden", boxShadow: "12px 0 42px rgba(122,78,58,0.08)" }}>
        <div style={{ padding: "16px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
          <BrandLogo size={40} compact={!sidebar} />
        </div>
        <nav style={{ flex: 1, padding: "8px 6px", overflowY: "auto" as any }}>
          {items.map(([path, label, icon]: any) => (
            <NavLink key={path} to={path} aria-label={label} title={label} style={({ isActive }) => ({ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 12, marginBottom: 3, textDecoration: "none", background: isActive ? C.soft : "transparent", color: isActive ? C.primary : C.mid, fontWeight: isActive ? 800 : 600, fontSize: 13, whiteSpace: "nowrap" as any, overflow: "hidden", transition: "all .12s", border: `1px solid ${isActive ? C.accent : "transparent"}` })}>
              <span style={{ display: "inline-flex", flexShrink: 0 }}>{renderIcon(icon, 18)}</span>{sidebar && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>
        {sidebar && <div style={{ padding: "10px 8px", borderTop: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, cursor: "pointer", marginBottom: 4 }} onClick={() => nav("/profile")} onMouseEnter={e => (e.currentTarget.style.background = C.bg)} onMouseLeave={e => (e.currentTarget.style.background = "")}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.primary, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{user.name?.charAt(0)}</div>
            <div style={{ flex: 1, overflow: "hidden" }}><div style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap" as any, overflow: "hidden", textOverflow: "ellipsis" }}>{user.name}</div><div style={{ fontSize: 10, color: C.light }}>{roleLabel(user.role)}</div></div>
          </div>
          <button onClick={logout} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: "none", border: "none", cursor: "pointer", color: C.light, fontSize: 12, fontFamily: "inherit", width: "100%", borderRadius: 8 }}><Icon name="log-out" size={15} /> Sign out</button>
        </div>}
        <button aria-label={sidebar ? "Collapse sidebar" : "Expand sidebar"} onClick={() => setSidebar(!sidebar)} style={{ padding: 12, background: "none", border: "none", cursor: "pointer", color: C.light, fontSize: 14, borderTop: `1px solid ${C.border}`, display: "grid", placeItems: "center" }}>{sidebar ? <Icon name="chevron-left" size={18} /> : <Icon name="chevron-right" size={18} />}</button>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" as any, overflow: "hidden", minWidth: 0 }}>
        <header style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "0 clamp(12px,2vw,22px)", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexShrink: 0, boxShadow: "0 1px 6px rgba(127,39,77,0.04)" }}>
          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <button
              className="mobile-menu-trigger"
              aria-label="Open navigation menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
              style={{ width: 38, height: 38, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bg, color: C.primary, alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            >
              <Icon name="layout-dashboard" size={18} />
            </button>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 900, color: C.light, letterSpacing: "0.08em", textTransform: "uppercase" as any }}>{roleLabel(user.role)}</div>
              <div className="cc-truncate" style={{ fontSize: 18, fontWeight: 900, color: C.primary, lineHeight: 1.15 }}>{pageTitle}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}><CommandPalette role={user.role} /><ContextHelpButton onClick={() => setTourOpen(true)} label="Restart Creamy Guide tour" /><NotifBell /></div>
        </header>
        <main className="cc-app-main" style={{ flex: 1, overflowY: "auto" as any, overflowX: "hidden" as any, padding: "clamp(14px,2vw,22px) clamp(12px,2vw,22px) 80px", minWidth: 0 }}>
          <div className="cc-guide-shell">
            <GuidePanel pageKey={pageKey} onStartTour={() => setTourOpen(true)} />
          </div>
          <Outlet />
        </main>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-drawer-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setMobileMenuOpen(false); }}>
          <aside className="mobile-drawer" aria-label="Mobile navigation menu">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <BrandLogo size={38} />
              <button aria-label="Close navigation menu" onClick={() => setMobileMenuOpen(false)} style={{ width: 36, height: 36, borderRadius: 11, border: `1px solid ${C.border}`, background: C.bg, color: C.mid, cursor: "pointer", display: "grid", placeItems: "center" }}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <nav style={{ display: "grid", gap: 6 }}>
              {items.map(([path, label, icon]: any) => {
                const active = loc.pathname === path;
                return (
                  <button
                    key={path}
                    aria-label={label}
                    onClick={() => goTo(path)}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 12px", borderRadius: 14, border: `1px solid ${active ? C.accent : C.border}`, background: active ? C.soft : C.surface, color: active ? C.primary : C.mid, fontFamily: "inherit", fontSize: 13, fontWeight: active ? 800 : 650, cursor: "pointer", textAlign: "left" as any }}
                  >
                    <span style={{ display: "inline-flex", flexShrink: 0 }}>{renderIcon(icon, 18)}</span>
                    <span className="cc-truncate">{label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Mobile bottom nav */}
      <nav className="mobile-nav" aria-label="Primary mobile navigation" style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: C.surface, borderTop: `1px solid ${C.border}`, zIndex: 100, padding: "8px 0 max(8px,env(safe-area-inset-bottom))", boxShadow: "0 -4px 20px rgba(127,39,77,0.08)" }}>
        {(items.length > 5 ? items.slice(0, 4) : items).map(([path, label, icon]: any) => {
          const active = loc.pathname === path;

          return (
            <button
              key={path}
              aria-label={label}
              onClick={() => goTo(path)}
              style={{ flex: 1, display: "flex", flexDirection: "column" as any, alignItems: "center", gap: 3, padding: "4px 0", background: "none", border: "none", cursor: "pointer", color: active ? C.primary : C.light, fontFamily: "inherit", minHeight: 44 }}
            >
              <span style={{ display: "inline-flex" }}>{renderIcon(icon, 22)}</span>
              <span style={{ fontSize: 9, fontWeight: active ? 800 : 500 }}>{label}</span>
              {active && <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.primary }} />}
            </button>
          );
        })}

        {items.length > 5 && (
          <button
            aria-label="More navigation"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(true)}
            style={{ flex: 1, display: "flex", flexDirection: "column" as any, alignItems: "center", gap: 3, padding: "4px 0", background: "none", border: "none", cursor: "pointer", color: C.light, fontFamily: "inherit", minHeight: 44 }}
          >
            <span style={{ display: "inline-flex" }}>{renderIcon("menu", 22)}</span>
            <span style={{ fontSize: 9, fontWeight: 600 }}>More</span>
          </button>
        )}
      </nav>

      {/* Quick Actions */}
      {!tourOpen && <QuickActions onNewOrder={() => setNewOrd(true)} />}
      <OnboardingTour enabled={tourOpen} onClose={() => setTourOpen(false)} />
      {newOrd && <NewOrderForm onClose={() => setNewOrd(false)} onSuccess={() => qc.invalidateQueries({ queryKey: ["orders"] })} />}
    </div>
  );
};

export default AppShell;
